import { useLocale, useTranslate } from "@lobby/ocb-intl";
import { createElement, memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { queryClient } from "../../app";
import { Chat, Player } from "../../entities";
import {
  Button,
  Icon,
  Skeleton,
  Spinner,
  getChatAttachmentSrc,
  last,
  useLobbyCloseBtn,
} from "../../shared";

import { formatDate } from "./helpers";

import type { ApiClientResponse } from "@lobby/api-client";
import type { HTMLAttributes, ChangeEvent } from "react";
import type { ComponentType } from "react";

type PendingMessage = {
  text: string;
  timestamp: number;
};

interface ConversationWindowProps {
  conversationId: number | null;
  pendingMessages: PendingMessage[];
  isChatWindowExpanded: boolean;
  onError: (error: { message: string; code: number }) => void;
  // onMessagesLoading: (isMessagesLoaded: boolean) => void;
}

export const ConversationWindow = memo(function ConversationWindow({
  conversationId,
  pendingMessages,
  isChatWindowExpanded,
  onError,
  // onMessagesLoading,
}: ConversationWindowProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isScrollAtBottom = useRef(false);
  const lastScrollAreaHeight = useRef(0);

  const player = Player.usePlayer();
  const conversations = Chat.useConversationList();
  const {
    data: messagesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isMessagesLoading,
  } = Chat.useConversationHistory(conversationId);
  const locale = useLocale();

  const messages = useMemo(
    () => messagesData?.pages.flatMap((page) => page.result).reverse() ?? [],
    [messagesData],
  );

  const messagesExist = messages.length || pendingMessages.length > 0;

  const conversation = conversations.data?.result?.[0] ?? null;
  const hasUnreadMessages = !(conversation?.isRead ?? true);

  function scrollToBottom() {
    scrollContainerRef.current?.scrollTo(0, scrollContainerRef.current.scrollHeight);
  }

  useLayoutEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    if (!isFetchingNextPage) {
      scrollContainer.scrollTo(0, scrollContainer.scrollHeight - lastScrollAreaHeight.current);
    }

    lastScrollAreaHeight.current = scrollContainer.scrollHeight ?? 0;
  }, [isFetchingNextPage]);

  useLayoutEffect(() => {
    // onMessagesLoading(!isMessagesLoading);
    scrollToBottom();
  }, [isMessagesLoading]);

  useLayoutEffect(() => {
    if (isScrollAtBottom.current || pendingMessages.length > 0) {
      scrollToBottom();
    }
  }, [messages, pendingMessages.length, isChatWindowExpanded]);

  useEffect(() => {
    const error = last(messagesData?.pages)?.error;
    if (error) {
      onError(error);
    }
  }, [messagesData, onError]);

  useEffect(() => {
    if (hasUnreadMessages) {
      /* After fetching new messages (Conversation.getMessages), server marks them as read,
         and we need to make the same changes in the local cache to keep the data consistent.
         @see https://git.casinomodule.org/casino23/tickets/-/issues/3035#note_211671
      */
      queryClient.setQueryData<ApiClientResponse<"Conversation.getList">>(
        ["Conversation.getList"],
        (prevData) => {
          if (!prevData || !prevData.result) return prevData;

          prevData.result[0].isRead = true;

          return {
            ...prevData,
            result: {
              ...prevData.result,
            },
          };
        },
      );

      if (queryClient.isFetching({ queryKey: ["Conversation.getMessages"] }) === 0) {
        queryClient.invalidateQueries({
          queryKey: ["Conversation.getMessages"],
        });
      }
    }
  }, [messagesData, hasUnreadMessages]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const controller = new AbortController();

    scrollContainer.addEventListener(
      "scroll",
      () => {
        isScrollAtBottom.current =
          scrollContainer.scrollTop + scrollContainer.clientHeight === scrollContainer.scrollHeight;

        if (scrollContainer.scrollTop === 0) {
          if (!hasNextPage || isFetchingNextPage) {
            return;
          }
          fetchNextPage();
        }
      },
      {
        signal: controller.signal,
      },
    );

    return () => {
      controller.abort();
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, messagesExist]);

  return (
    <div className="support-chat-window__conversation-window">
      {messagesExist ? (
        <div
          ref={scrollContainerRef}
          className="relative h-full overflow-y-auto overscroll-contain p-2.5"
        >
          {isFetchingNextPage && (
            <div className="pointer-events-none absolute top-5 right-0 left-0 flex items-center justify-center">
              <Icon className="animate-spin text-20 text-accent" name="reload" />
            </div>
          )}
          <ul id="chat-history-list" className="space-y-2.5">
            {messages.map((message) => (
              <li key={message.seqNum}>
                <ChatMessage
                  sender={message.from.userName}
                  message={message.text}
                  messageType={message.type}
                  timestamp={message.createdAt * 1000}
                  isFromCurrentUser={message.from.userId === player.data?.id}
                  locale={locale}
                />
              </li>
            ))}

            {pendingMessages.map((pendingMsg, i) => (
              <li key={i} style={{ opacity: 0.5 }}>
                <ChatMessage
                  sender={(player.data?.displayableName as string) ?? ""}
                  message={pendingMsg.text}
                  messageType="text"
                  timestamp={pendingMsg.timestamp}
                  locale={locale}
                  isFromCurrentUser
                />
              </li>
            ))}
          </ul>
        </div>
      ) : isMessagesLoading ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <EmptyChatPlaceholder />
      )}
    </div>
  );
});

interface ChatMessageProps {
  sender: string;
  message: string;
  messageType: "text" | "image" | "video";
  locale: string;
  isFromCurrentUser: boolean;
  timestamp: number;
}

function ChatMessage({
  sender,
  message,
  messageType,
  locale,
  timestamp,
  isFromCurrentUser,
}: ChatMessageProps) {
  const [isMediaViewerOpen, setIsMediaViewerOpen] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const handleMediaClick = () => {
    setIsMediaViewerOpen(true);
  };

  return (
    <>
      <div className={`support-chat-message ${isFromCurrentUser ? "-user" : "-support"}`}>
        <span className="support-chat-message__sender-name">{sender}</span>

        {messageType === "text" && <p>{message}</p>}

        {messageType === "image" && (
          <div className="support-chat-message__media-preview" onClick={handleMediaClick}>
            {!isImageLoaded && <Skeleton />}
            <img
              src={getChatAttachmentSrc(message)}
              alt="Image"
              onLoad={() => setIsImageLoaded(true)}
              onError={() => setIsImageLoaded(true)}
              style={{ display: isImageLoaded ? "block" : "none" }}
            />
          </div>
        )}

        {messageType === "video" && (
          <div className="support-chat-message__media-preview" onClick={handleMediaClick}>
            {!isVideoLoaded && <Skeleton />}
            <video
              src={getChatAttachmentSrc(message)}
              muted
              onLoadedData={() => setIsVideoLoaded(true)}
              onError={() => setIsVideoLoaded(true)}
              style={{ display: isVideoLoaded ? "block" : "none" }}
            />
            {isVideoLoaded && (
              <div className="support-chat-message__media-preview__play-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="1em"
                  height="1em"
                  viewBox="0 0 48 48"
                >
                  <circle cx="50%" cy="50%" r="50%" fill="rgba(0, 0, 0, 0.8)" />
                  <path d="M18 14v20l16-10z" fill="white" />
                </svg>
              </div>
            )}
          </div>
        )}

        <span className="support-chat-message__timestamp">{formatDate(timestamp, locale)}</span>
      </div>

      <MediaViewerDialog
        mediaUrl={messageType !== "text" ? message : null}
        mediaType={messageType !== "text" ? messageType : null}
        isOpen={isMediaViewerOpen}
        onClose={() => setIsMediaViewerOpen(false)}
      />
    </>
  );
}

export function SendMessageBtn({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: HTMLAttributes<HTMLButtonElement>["onClick"];
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      className="support-chat-window__send-message-btn"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 20">
        <path
          fill="currentColor"
          d="M.68 10.836c.885.31 2.041.729 3.577 1.166 1.545.447 2.274.525 3.84-.496 1.564-1.02 6.968-4.714 7.649-5.19.68-.466 2.002-1.244 2.255-1.06.252.185-.321 1.06-1.186 1.896-.865.836-4.656 4.325-5.599 5.268-.943.943-.933 1.633.04 2.42.961.788 1.311.933 5.131 3.548 3.82 2.615 4.724 1.74 5.093-.428.37-2.167 2.07-13.18 2.382-15.318.165-1.098.291-1.934-.292-2.226-.573-.291-1.254-.136-2.624.438-2.78 1.176-17.7 7.25-19.556 8.038-1.857.777-1.594 1.633-.71 1.944Z"
        />
      </svg>
    </button>
  );
}

export function EmptyChatPlaceholder() {
  const { $t } = useTranslate();

  return (
    <div className="support-chat-window-nodata-placeholder">
      <svg
        className="support-chat-window-nodata-placeholder__icon"
        xmlns="http://www.w3.org/2000/svg"
        width="1em"
        height="1em"
        viewBox="0 0 112 112"
      >
        <path
          fill="currentColor"
          strokeWidth="3"
          d="m10.23 49.39-.25 1.7h6.82a4.1 4.1 0 0 1 4.1 4.1V84a4.1 4.1 0 0 1-4.1 4.1h-5.6c-5.46 0-9.7-4.1-9.7-8.9V56A54.56 54.56 0 0 1 56 1.5 54.56 54.56 0 0 1 110.5 56v23.2c0 4.8-4.25 8.9-9.7 8.9h-1.5v1.5c0 11.53-9.37 20.9-20.9 20.9H46.3V96.7h19.4v5.6h12.7c7 0 12.7-5.7 12.7-12.7V55.2a4.1 4.1 0 0 1 4.1-4.1h6.82l-.25-1.71A46.32 46.32 0 0 0 56 9.7a46.32 46.32 0 0 0-45.77 39.69Z"
        />
        <path
          fill="currentColor"
          d="M67.17 70.57a950.24 950.24 0 0 1 1.83-8.7l.84-1.05 1.55-7.37A1097.8 1097.8 0 0 1 73.18 45l6.02-2.63a1252.58 1252.58 0 0 0-5.27 5.84l-5.6 6.24a456.47 456.47 0 0 0-4.54 5.04l-.27-.99h8.2l.83-.57h8.84l-1.4 6.9h-24.1l1.1-5.36 2.26-2.52 2.52-2.82c.86-.93 1.64-1.8 2.36-2.6l4.34-4.85 2.27-2.52 2.27-2.52h8.34a4065.6 4065.6 0 0 0-2.12 10.1L77.1 61.85a5782.42 5782.42 0 0 0-1.82 8.72h-8.1ZM30 70.57l1.16-5.38a621 621 0 0 0 2.47-1.78c.81-.6 1.64-1.18 2.48-1.77a152.7 152.7 0 0 0 5.06-3.8 24.2 24.2 0 0 0 3.53-3.27 7.42 7.42 0 0 0 1.7-3.22c.24-1.06.08-1.93-.48-2.6-.55-.67-1.45-1-2.71-1-.62 0-1.27.08-1.95.26-.69.19-1.4.43-2.13.74-.72.3-1.47.66-2.22 1.07-.74.4-1.5.81-2.25 1.26l.95-7.98c.68-.3 1.39-.57 2.12-.82.73-.25 1.48-.47 2.27-.65a22.6 22.6 0 0 1 5.27-.63c2.42 0 4.4.44 5.96 1.32a7.16 7.16 0 0 1 3.28 3.64c.63 1.52.73 3.26.31 5.2-.3 1.45-.85 2.78-1.64 4.02a17.18 17.18 0 0 1-3.23 3.6 44.47 44.47 0 0 1-5.19 3.79l-4.43 2.85-.82-1.74h12.86l-1.45 6.89H30Z"
        />
      </svg>
      <div>{$t({ defaultMessage: "Have a question? Ask us!" })}</div>
    </div>
  );
}

export function AddAttachmentBtn({ onFileSelect }: { onFileSelect: (file: File) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
      // Reset input value to allow selecting the same file again
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  return (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      onMouseDown={(e) => e.preventDefault()}
      className="support-chat-window__add-attachment-btn"
    >
      <input
        ref={inputRef}
        type="file"
        accept=".jpeg, .jpg, .png, .mp4, .mov, .avi"
        onChange={handleFileChange}
        className="hidden"
      />
      <Icon name="paperClip" />
    </button>
  );
}

function renderCloseButton(
  CloseBtn: ComponentType<{ onClick: () => void }> | null | undefined,
  onClick: () => void,
) {
  if (CloseBtn) {
    return createElement(CloseBtn, { onClick });
  }

  return (
    <Button variant="secondary" fit onClick={onClick}>
      <Icon className="text-sm lg:text-base" name="close" />
    </Button>
  );
}

interface AttachmentPreviewDialogProps {
  file: File | null;
  isOpen: boolean;
  isUploading: boolean;
  error: string | null;
  onSend: () => void;
  onCancel: () => void;
}

export function AttachmentPreviewDialog({
  file,
  isOpen,
  isUploading,
  error,
  onSend,
  onCancel,
}: AttachmentPreviewDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const CloseBtn = useLobbyCloseBtn();

  const isVideo = file?.type.startsWith("video/");
  const isImage = file?.type.startsWith("image/");
  const previewUrl = useMemo(() => {
    if (!file || !isOpen) {
      return null;
    }

    return URL.createObjectURL(file);
  }, [file, isOpen]);

  useEffect(() => {
    if (isOpen && dialogRef.current && !dialogRef.current.open) {
      dialogRef.current.showModal();
    } else if (!isOpen && dialogRef.current?.open) {
      dialogRef.current.close();
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Capture first frame of video
  useEffect(() => {
    if (isVideo && videoRef.current && previewUrl) {
      const video = videoRef.current;
      video.currentTime = 0.1; // Seek to first frame
    }
  }, [isVideo, previewUrl]);

  if (!file) return null;

  return (
    <dialog ref={dialogRef} className="attachment-preview-dialog dialog" onClose={onCancel}>
      <div className="attachment-preview-dialog__content">
        <div className="attachment-preview-dialog__header">
          {renderCloseButton(CloseBtn, onCancel)}
        </div>

        <div className="attachment-preview-dialog__preview">
          {isImage && previewUrl && <img src={previewUrl} alt="Preview" />}
          {isVideo && previewUrl && (
            <video ref={videoRef} src={previewUrl} controls={false} muted />
          )}
        </div>
        <div className="attachment-preview-dialog__preview-details">
          <span>{file.name}</span>
          <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
        </div>

        {error && <div className="attachment-preview-dialog__error">{error}</div>}

        <div className="attachment-preview-dialog__actions">
          <button type="button" onClick={onSend} disabled={isUploading} data-button="send">
            {isUploading ? (
              <Icon className="animate-spin" name="reload" />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 20">
                <path
                  fill="currentColor"
                  d="M.68 10.836c.885.31 2.041.729 3.577 1.166 1.545.447 2.274.525 3.84-.496 1.564-1.02 6.968-4.714 7.649-5.19.68-.466 2.002-1.244 2.255-1.06.252.185-.321 1.06-1.186 1.896-.865.836-4.656 4.325-5.599 5.268-.943.943-.933 1.633.04 2.42.961.788 1.311.933 5.131 3.548 3.82 2.615 4.724 1.74 5.093-.428.37-2.167 2.07-13.18 2.382-15.318.165-1.098.291-1.934-.292-2.226-.573-.291-1.254-.136-2.624.438-2.78 1.176-17.7 7.25-19.556 8.038-1.857.777-1.594 1.633-.71 1.944Z"
                />
              </svg>
            )}
          </button>
        </div>
      </div>
    </dialog>
  );
}

interface MediaViewerDialogProps {
  mediaUrl: string | null;
  mediaType: "image" | "video" | null;
  isOpen: boolean;
  onClose: () => void;
}

function MediaViewerDialog({ mediaUrl, mediaType, isOpen, onClose }: MediaViewerDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const CloseBtn = useLobbyCloseBtn();

  useEffect(() => {
    if (isOpen && dialogRef.current && !dialogRef.current.open) {
      dialogRef.current.showModal();
    } else if (!isOpen && dialogRef.current?.open) {
      dialogRef.current.close();
    }
  }, [isOpen]);

  if (!mediaUrl || !mediaType) return null;

  return (
    <dialog ref={dialogRef} className="media-viewer-dialog dialog" onClose={onClose}>
      <div className="media-viewer-dialog__content">
        <div className="media-viewer-dialog__header">{renderCloseButton(CloseBtn, onClose)}</div>

        <div className="media-viewer-dialog__media-container">
          {mediaType === "image" && <img src={getChatAttachmentSrc(mediaUrl)} alt="Media" />}
          {mediaType === "video" && (
            <video src={getChatAttachmentSrc(mediaUrl)} controls autoPlay={false} />
          )}
        </div>
      </div>
    </dialog>
  );
}
